import React from "react";
import { Box, type BoxProps, Flex, Heading, Image, Text } from "@chakra-ui/react";
import { MotionReveal } from "./MotionReveal";

export interface PageHeaderProps extends Omit<BoxProps, "title"> {
  /** "hero" (dashboard only: display title, subtitle, meta, overlapping children) or "strip" (default: slim 88px band). */
  variant?: "hero" | "strip";
  /** Uppercase eyebrow, e.g. "DASHBOARD". */
  eyebrow: string;
  /** May contain <Highlight onDark>. */
  title: React.ReactNode;
  /** hero only — customer name line (h3 600 white, one line). */
  subtitle?: React.ReactNode;
  /** hero only — caption line, e.g. "{branch} · {terms}". */
  meta?: React.ReactNode;
  /** Right slot: primary CTA, SearchField + FilterButton… wraps beneath the title on base. */
  actions?: React.ReactNode;
  /** Faint wordmark glyph bottom-right (default true for hero, false for strip). */
  showGlyph?: boolean;
  /** hero only — a row of StatCards that overlaps the band's bottom edge. */
  children?: React.ReactNode;
}

/**
 * Navy page band with the green radial glow. One per page.
 * @example
 * <PageHeader variant="hero" eyebrow="DASHBOARD" title={<>Welcome <Highlight onDark>back</Highlight></>} subtitle={name} meta={`${branch} · ${terms}`} actions={<Button variant="primary" leftIcon={<FiPlus/>}>New application</Button>}>
 *   <SimpleGrid columns={{ base: 1, sm: 3 }} gap={4}>…StatCards…</SimpleGrid>
 * </PageHeader>
 * @example <PageHeader variant="strip" eyebrow="CONTRACTS" title="My contracts" actions={<><SearchField … /><FilterButton … /></>} />
 */
export const PageHeader: React.FC<PageHeaderProps> = ({
  variant = "strip",
  eyebrow,
  title,
  subtitle,
  meta,
  actions,
  showGlyph,
  children,
  ...rest
}) => {
  const isHero = variant === "hero";
  const glyph = showGlyph ?? isHero;
  const hasOverlap = isHero && Boolean(children);

  return (
    <Box {...rest}>
      <Box
        as="header"
        layerStyle="hero"
        position="relative"
        overflow="hidden"
        px={{ base: 5, md: 8 }}
        py={isHero ? { base: 6, md: 8 } : 5}
        pb={hasOverlap ? { base: "5rem", md: "5.5rem" } : undefined}
        minH={isHero ? undefined : "88px"}
        display="flex"
        alignItems="center"
      >
        {glyph && (
          <Image
            src={`${process.env.PUBLIC_URL ?? ""}/assets/Logo.png`}
            alt=""
            aria-hidden
            position="absolute"
            right="-2rem"
            bottom="-3rem"
            h="14rem"
            w="auto"
            opacity={0.08}
            pointerEvents="none"
            display={{ base: "none", md: "block" }}
            draggable={false}
          />
        )}
        <Flex
          as={MotionReveal}
          w="full"
          position="relative"
          zIndex={1}
          direction={{ base: "column", md: "row" }}
          align={{ base: "stretch", md: isHero ? "flex-end" : "center" }}
          justify="space-between"
          gap={4}
        >
          <Box minW={0}>
            <Text textStyle="label" color="navy.200">
              {eyebrow}
            </Text>
            <Heading as="h1" size={isHero ? "xl" : "lg"} color="white" mt={isHero ? 2 : 1}>
              {title}
            </Heading>
            {isHero && subtitle && (
              <Text textStyle="h3" fontWeight={600} color="white" mt={2} noOfLines={1}>
                {subtitle}
              </Text>
            )}
            {isHero && meta && (
              <Text textStyle="caption" color="navy.200" mt={2}>
                {meta}
              </Text>
            )}
          </Box>
          {actions && (
            <Flex gap={3} wrap="wrap" align="center" flexShrink={0} sx={{ "& > *": { flexGrow: { base: 1, md: 0 } } }}>
              {actions}
            </Flex>
          )}
        </Flex>
      </Box>
      {hasOverlap && (
        <Box mt="-3rem" px={{ base: 2, md: 6 }} position="relative" zIndex={1}>
          {children}
        </Box>
      )}
    </Box>
  );
};
